"use client";

import { createContext, FC, ReactNode, useContext, useMemo, useState } from "react";

import type { OfficeContactPrefillParams } from "utils/officeContact";

type OfficeLocationSlug = { slug: string };

type OfficeContactPrefillContextValue = {
  officeSlug: string | undefined;
  officeName: string | undefined;
  locationSlug: string | undefined;
  locationCount: number;
  activeLocationSlug: string | undefined;
  setActiveLocationSlug: (slug: string) => void;
};

const OfficeContactPrefillContext = createContext<OfficeContactPrefillContextValue | undefined>(
  undefined,
);

export const OfficeContactPrefillProvider: FC<{
  officeSlug: string | undefined;
  officeName?: string;
  officeLocations?: OfficeLocationSlug[];
  children: ReactNode;
}> = ({ officeSlug, officeName, officeLocations, children }) => {
  const locationCount = officeLocations?.length ?? 0;
  const defaultLocationSlug = locationCount > 1 ? officeLocations?.[0]?.slug : undefined;
  const [activeLocationSlug, setActiveLocationSlug] = useState<string | undefined>(
    defaultLocationSlug,
  );

  const locationSlug = locationCount > 1 ? activeLocationSlug : undefined;

  const value = useMemo(
    () => ({
      officeSlug,
      officeName,
      locationSlug,
      locationCount,
      activeLocationSlug,
      setActiveLocationSlug,
    }),
    [officeSlug, officeName, locationSlug, locationCount, activeLocationSlug],
  );

  return (
    <OfficeContactPrefillContext.Provider value={value}>
      {children}
    </OfficeContactPrefillContext.Provider>
  );
};

/** Brand office slug for Kantoor prefill (`?office=`). */
export const useOfficeContactPrefillQuery = (): OfficeContactPrefillParams | undefined => {
  const context = useContext(OfficeContactPrefillContext);
  if (!context?.officeSlug) {
    return undefined;
  }

  return {
    officeSlug: context.officeSlug,
    locationSlug: context.locationSlug,
    locationCount: context.locationCount,
  };
};

export const useOfficeName = (): string | undefined => {
  const context = useContext(OfficeContactPrefillContext);
  return context?.officeName;
};

export const useOfficeContactPrefillLocation = (): Pick<
  OfficeContactPrefillContextValue,
  "activeLocationSlug" | "setActiveLocationSlug"
> => {
  const context = useContext(OfficeContactPrefillContext);
  return {
    activeLocationSlug: context?.activeLocationSlug,
    setActiveLocationSlug: context?.setActiveLocationSlug ?? (() => {}),
  };
};
